<template>
  <div class="fund-status-container">
    <template v-if="statusLoading">
      <mds-loader />
    </template>
    <template v-else-if="errorMsg">
     <div class="notes-list-error">
        <p>Something went wrong! We could not load the 'Fund Status by Client'.</p>
        <p>Please refresh the page or try again later.</p>
      </div>
    </template>
    <template v-else>
      <mds-section
        class="fund-section"
        :title="fundDetails.displayName"
        container
        bold
      >
        <template v-slot:mds-section-supplemental-content>
          <div class="section-supplemental-container">
            <div class="section-ticker">
              <span class="section-name">Ticker: </span>
              <span class="section-value">{{fundDetails.ticker}}</span>
            </div>
            <div class="section-status">
              <span class="section-name">Current Status: </span>
              <span class="section-value status-label-icon">
                <span class="status-label">{{fundDetails.fundStatuses[0].currentStatus.name}}</span>
                <span>
                  <StatusIcons
                    :status="[{ name: fundDetails.fundStatuses[0].currentStatus.name }]"
                  />
                </span>
              </span>
            </div>
          </div>
        </template>
        <template v-if="fiduciaryClientPresent">
          <mds-section
            class="fiduciary-section"
            v-for="(fiduciary, index) in fundDetails.fiduciaryClientsSameStatuses"
            :key="fundDetails.fiduciaryFundID + fiduciary.id"
            :title="fiduciary.fiduciaryName"
            :expanded="index === 0"
            :size="6"
            border="none"
            collapsible
            bold
          >
            <template>
              <mds-table>
                <mds-thead>
                  <mds-th>Clients</mds-th>
                  <mds-th right-aligned>Current Status</mds-th>
                </mds-thead>
                <mds-tbody>
                  <mds-tr
                    v-for="(clientStatus, index) in fiduciary.fundStatuses"
                    :key="clientStatus.client.id + index"
                  >
                    <mds-td>{{clientStatus.client.clientName}}</mds-td>
                    <mds-td right-aligned>
                      <div class="status-label-icon">
                        <span class="status-label">{{clientStatus.currentStatus.name}}</span>
                        <span>
                          <StatusIcons
                            :status="[{ name: clientStatus.currentStatus.name }]"
                          />
                        </span>
                      </div>
                    </mds-td>
                  </mds-tr>
                </mds-tbody>
              </mds-table>
            </template>
          </mds-section>
        </template>
        <template v-else>
          <!-- eslint-disable-next-line max-len -->
          <p class="fiduciary-message">There are no providers to show as this fund is not Approved/On Watch/Removed for any other provider.</p>
        </template>
      </mds-section>
    </template>
  </div>
</template>

<script>
import MdsSection from '@mds/section';
import {
  MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd,
} from '@mds/data-table';
import StatusIcons from '@/components/atoms/StatusIcons';
import * as FundReviewService from '@/services/FundReview';

export default {
  name: 'fund-status',
  components: {
    StatusIcons,
    MdsSection,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fundDetails: '',
      statusLoading: false,
      errorMsg: false,
    };
  },
  created() {
    this.loadFundStatusByClient();
  },
  computed: {
    fiduciaryClientPresent() {
      return this.fundDetails.fiduciaryClientsSameStatuses.length > 0;
    },
  },
  methods: {
    loadFundStatusByClient() {
      this.statusLoading = true;
      const { secId, clientId, currentStatusId } = this.data;
      const queryParams = `secId=${secId}&fiduciaryClientId=${clientId}&currentFundStatusId=${currentStatusId}`;
      FundReviewService.getFundStatusByClient(queryParams).then((response) => {
        this.statusLoading = false;
        if (response.status.success) {
          this.fundDetails = { ...response.data.list[0] };
        }
      }).catch((error) => {
        this.statusLoading = false;
        this.handleError(error);
      });
    },
    handleError(error) {
      const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        this.$router.push('/login');
      } else {
        this.errorMsg = true;
      }
    },
  },
};
</script>

<style lang="scss">
.fund-status-container {
  margin: 0 $mds-space-2-x;
  .notes-list-error {
    text-align: center;
    @include mds-body-text-l($bold: true);
  }
  .fund-section {
    margin-bottom: $mds-space-3-x;
    padding: 0;
    .fiduciary-message {
      margin-top: 0;
      padding-left: $mds-space-2-x;
      @include mds-body-text-m($bold: true);
    }
    .section-supplemental-container {
      display: flex;
      .section-ticker {
        margin-right: $mds-space-3-and-a-half-x;
      }
      .section-status {
        display: flex;
        .section-name {
          margin-right: $mds-space-half-x;
        }
      }
      .section-name {
        @include mds-body-text-m($bold: true);
      }
    }
    .fiduciary-section {
      border: none;
      box-shadow: none;
      margin-bottom: $mds-space-1-and-a-half-x;
      padding-bottom: $mds-space-1-and-a-half-x;
    }
    .status-label-icon {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .status-label {
        margin-right: $mds-space-1-x;
      }
    }
  }
}
</style>

<style lang="scss" module>
:global(.fund-section) {
  > .mds-section__header-container:first-of-type {
    margin: $mds-space-1-x $mds-space-3-x;
  }
  > .mds-section__content {
    padding: $mds-space-3-x $mds-space-1-x 0;
  }
}

:global(.fiduciary-section) {
  &.mds-section--collapsible {
    .mds-section__header-container {
      margin-top: $mds-space-quarter-x;
      .mds-section__toggle {
        transform: rotate(0deg);
        transition: transform 200ms cubic-bezier(0.5, 0, 0.5, 1);
      }
    }
  }
  &.mds-section--expanded {
    &.mds-section--collapsible {
      .mds-section__header-container {
        .mds-section__toggle {
          transform: rotate(90deg);
          transition: transform 200ms cubic-bezier(0.5, 0, 0.5, 1);
        }
      }
    }
  }
  .mds-section__content {
    padding: $mds-space-1-x $mds-space-2-x 0 $mds-space-2-and-a-half-x;
  }
}
</style>
